import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { Collapse } from '@mui/material';
// hooks
import useActiveLink from '../../../hooks/useActiveLink';
//
import { NavListProps } from '../types';
import NavItem from './NavItem';
import { roleHasPageAccess } from 'src/auth/rbac';
import { useUserRole } from 'src/hooks/useUserRole';
import { useOrg } from 'src/hooks/query/orgHooks';
import { orgHasPermission } from 'src/auth/fbac';

// ----------------------------------------------------------------------

type NavListRootProps = {
  data: NavListProps;
  depth: number;
  hasChild: boolean;
};

export default function NavList({ data, depth, hasChild }: NavListRootProps) {
  const { data: role } = useUserRole();
  const { data: org } = useOrg();

  const { pathname } = useLocation();

  const { active, isExternalLink } = useActiveLink(data.path);

  const [open, setOpen] = useState(active);

  useEffect(() => {
    if (!active) {
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleToggle = () => {
    setOpen(!open);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const orgHasAccess =
    !data.features ||
    data.features.length == 0 ||
    (org && !!data.features && orgHasPermission(org, data.features));

  return roleHasPageAccess(data.path, role) && orgHasAccess ? (
    <>
      <NavItem
        item={data}
        depth={depth}
        open={open}
        active={active}
        isExternalLink={isExternalLink}
        onClick={handleToggle}
        sx={{ px: 2 }}
      />

      {hasChild && (
        <Collapse in={open} unmountOnExit>
          <NavSubList data={data.children} depth={depth} />
        </Collapse>
      )}
    </>
  ) : (
    <></>
  );
}

// ----------------------------------------------------------------------

type NavListSubProps = {
  data: NavListProps[];
  depth: number;
};

function NavSubList({ data, depth }: NavListSubProps) {
  const { data: role } = useUserRole();
  return (
    <>
      {data.map(
        (list) =>
          roleHasPageAccess(list.path, role) && (
            <NavList
              key={list.title + list.path}
              data={list}
              depth={depth + 1}
              hasChild={!!list.children}
            />
          )
      )}
    </>
  );
}
